import { useEffect, useRef, useState, type ReactNode } from 'react';

const LEAVE_MS = 500;

interface Layer {
  key: string;
  node: ReactNode;
}

/**
 * Cross-fades between screens using CSS transitions. Whenever `screenKey` changes the previous
 * screen (frozen at its last render) fades out while the new one fades in.
 */
export function ScreenTransition({ screenKey, children }: { screenKey: string; children: ReactNode }) {
  const [currentKey, setCurrentKey] = useState(screenKey);
  const [leaving, setLeaving] = useState<Layer[]>([]);
  const [entered, setEntered] = useState(true);
  const lastNode = useRef<ReactNode>(children);

  // Adjust state during render when the key changes (React's recommended pattern).
  if (screenKey !== currentKey) {
    const prev: Layer = { key: currentKey, node: lastNode.current };
    setLeaving((l) => [...l.filter((x) => x.key !== prev.key && x.key !== screenKey), prev]);
    setCurrentKey(screenKey);
    setEntered(false);
  }

  useEffect(() => {
    lastNode.current = children;
  });

  useEffect(() => {
    if (entered) return;
    // Two frames so the browser paints the "enter" state before transitioning to "active".
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [entered, currentKey]);

  useEffect(() => {
    if (leaving.length === 0) return;
    const t = window.setTimeout(() => setLeaving([]), LEAVE_MS);
    return () => window.clearTimeout(t);
  }, [leaving]);

  return (
    <div className="relative h-full w-full">
      {leaving
        .filter((l) => l.key !== currentKey)
        .map((l) => (
          <div key={l.key} className="screen-layer" data-phase="leave" aria-hidden="true">
            {l.node}
          </div>
        ))}
      <div key={currentKey} className="screen-layer" data-phase={entered ? 'active' : 'enter'}>
        {children}
      </div>
    </div>
  );
}
